import { useState, useRef, useEffect } from 'react';
import { useAffiliateDisplay, type AffiliateDisplayMode } from '../../contexts/AffiliateDisplayContext';

const DISPLAY_MODE_OPTIONS: { value: AffiliateDisplayMode; label: string }[] = [
  { value: 'inline-card', label: 'Inline Card' },
  { value: 'sticky-banner', label: 'Sticky Banner' },
  { value: 'tooltip', label: 'Tooltip' },
  { value: 'collapsed', label: 'Collapsed' },
];

export function AffiliateDisplayToggle() {
  const { displayMode, setDisplayMode, isDevModeEnabled } = useAffiliateDisplay();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Only render in dev mode
  if (!isDevModeEnabled) {
    return null;
  }

  const currentLabel = DISPLAY_MODE_OPTIONS.find((opt) => opt.value === displayMode)?.label || 'Display';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
        title="Affiliate Display Mode (Dev Only)"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <span>{currentLabel}</span>
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 glass rounded-xl shadow-lg border border-black/5 dark:border-white/10 py-2 z-50 animate-fade-in">
          <div className="px-3 py-1.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide">
            Affiliate Display (Dev)
          </div>
          {DISPLAY_MODE_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setDisplayMode(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${
                displayMode === option.value
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-parchment-text/80 dark:text-white/80'
              }`}
            >
              <span
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  displayMode === option.value
                    ? 'border-primary-500'
                    : 'border-parchment-300 dark:border-white/30'
                }`}
              >
                {displayMode === option.value && (
                  <span className="w-2 h-2 rounded-full bg-primary-500" />
                )}
              </span>
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
